import { useEffect, useState } from 'react';
import { MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import { images, featuredProjects } from '../mock/data';

const LOGO_VIDEO_URL = process.env.REACT_APP_LOGO_VIDEO_URL;

export const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
  const [currentProjectImageIndex, setCurrentProjectImageIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Reset image index when project changes
  useEffect(() => {
    setCurrentProjectImageIndex(0);
  }, [currentProjectIndex]);

  const galleryImages = [
    { url: images.about, title: "Backyard Koi Pond", type: "Ecosystem Pond" },
    { url: images.hero, title: "Natural Waterfall Feature", type: "Waterfall" },
    { url: images.projects[0], title: "Clear Water with Visible Koi", type: "Koi Pond" },
    { url: images.projects[1], title: "Evening Water Garden", type: "Water Garden" },
    { url: images.projects[2], title: "Backyard Stream & Waterfall", type: "Stream" },
    { url: images.projects[3], title: "Vibrant Koi Ecosystem", type: "Ecosystem Pond" }
  ];

  const currentProject = featuredProjects[currentProjectIndex];

  const nextProjectImage = () => {
    setCurrentProjectImageIndex((prev) => 
      prev === currentProject.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevProjectImage = () => {
    setCurrentProjectImageIndex((prev) => 
      prev === 0 ? currentProject.images.length - 1 : prev - 1
    );
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero */}
      <section className="pt-32 pb-16 px-6 lg:px-12 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-light text-stone-800 mb-8 tracking-tight">
            Project Gallery
          </h1>
          <p className="text-xl text-stone-600 leading-relaxed">
            Explore our completed water environments - from multi-cascade waterfalls to natural fish ponds
          </p>
        </div>
      </section>

      {/* Project Selector Tabs */}
      <section className="py-8 px-6 lg:px-12 bg-stone-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            {featuredProjects.map((project, index) => (
              <button
                key={project.id}
                onClick={() => setCurrentProjectIndex(index)}
                className={`px-6 py-3 rounded-sm transition-all duration-300 ${
                  currentProjectIndex === index
                    ? 'bg-stone-900 text-white shadow-lg'
                    : 'bg-white text-stone-600 hover:bg-stone-200'
                }`}
                data-testid={`project-tab-${index}`}
              >
                <span className="font-medium">{project.name}</span>
                <span className="block text-xs mt-1 opacity-70">{project.location}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Project */}
      <section className="py-16 px-6 lg:px-12 bg-stone-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-stone-400 uppercase tracking-widest text-sm mb-4">Featured Project</p>
            <h2 className="text-4xl md:text-5xl font-light text-white mb-4 tracking-tight">
              {currentProject.name}
            </h2>
            <div className="flex items-center justify-center gap-2 text-stone-400">
              <MapPin size={18} />
              <span>{currentProject.location}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image Carousel */}
            <div className="relative">
              <div className="relative h-[500px] rounded-sm overflow-hidden shadow-2xl">
                <img
                  src={currentProject.images[currentProjectImageIndex]}
                  alt={`${currentProject.name} - Image ${currentProjectImageIndex + 1}`}
                  className="w-full h-full object-cover cursor-pointer transition-transform duration-500"
                  onClick={() => setSelectedImage({ 
                    url: currentProject.images[currentProjectImageIndex], 
                    title: currentProject.name,
                    type: currentProject.type
                  })}
                />
              </div>
              
              {/* Navigation Arrows */}
              <button
                onClick={prevProjectImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-lg"
                data-testid="prev-image-btn"
              >
                <ChevronLeft size={24} className="text-stone-800" />
              </button>
              <button
                onClick={nextProjectImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-lg"
                data-testid="next-image-btn"
              >
                <ChevronRight size={24} className="text-stone-800" />
              </button>

              {/* Image Indicators */}
              <div className="flex justify-center gap-2 mt-4">
                {currentProject.images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentProjectImageIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentProjectImageIndex 
                        ? 'bg-white w-8' 
                        : 'bg-stone-600 hover:bg-stone-500'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Project Details */}
            <div className="text-white">
              <p className="text-stone-400 uppercase tracking-wider text-sm mb-4">
                {currentProject.type}
              </p>
              <p className="text-lg text-stone-300 leading-relaxed mb-8">
                {currentProject.description}
              </p>
              
              <h4 className="text-sm uppercase tracking-wider text-stone-400 mb-4">Project Features</h4>
              <ul className="space-y-3">
                {currentProject.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3 text-stone-300">
                    <span className="w-2 h-2 bg-stone-500 rounded-full"></span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Project Image Thumbnails */}
      <section className="py-12 px-6 lg:px-12 bg-stone-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
            {currentProject.images.map((image, index) => (
              <button
                key={index}
                onClick={() => setCurrentProjectImageIndex(index)}
                className={`flex-shrink-0 w-32 h-24 rounded-sm overflow-hidden transition-all duration-300 ${
                  index === currentProjectImageIndex
                    ? 'ring-2 ring-white ring-offset-2 ring-offset-stone-800'
                    : 'opacity-60 hover:opacity-100'
                }`}
              >
                <img
                  src={image}
                  alt={`${currentProject.name} thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* More Projects Gallery Grid */}
      <section className="py-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-light text-stone-800 mb-12 text-center tracking-tight">
            More Water Environments
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {galleryImages.map((item, index) => (
              <div
                key={index}
                className="group relative h-96 rounded-sm overflow-hidden shadow-lg cursor-pointer"
                onClick={() => setSelectedImage(item)}
                data-testid={`gallery-image-${index}`}
              >
                <img
                  src={item.url}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900 via-stone-900/30 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <p className="text-sm text-stone-300 mb-2">{item.type}</p>
                  <h3 className="text-xl font-light">{item.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Logo Animation - Bottom of Page */}
      <section className="py-12 px-6 lg:px-12 bg-white">
        <div className="max-w-2xl mx-auto">
          <div className="relative rounded-sm overflow-hidden shadow-lg">
            <video
              className="w-full h-auto"
              autoPlay
              playsInline
            >
              <source src={LOGO_VIDEO_URL} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-6"
          onClick={() => setSelectedImage(null)}
          data-testid="lightbox-overlay"
        >
          <div className="relative max-w-6xl w-full">
            <img
              src={selectedImage.url}
              alt={selectedImage.title}
              className="w-full h-auto rounded-sm"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black to-transparent text-white">
              <p className="text-sm text-stone-300 mb-2">{selectedImage.type}</p>
              <h3 className="text-2xl font-light">{selectedImage.title}</h3>
            </div>
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 text-white hover:text-stone-300 text-4xl"
              data-testid="close-lightbox-btn"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
